/**
 * DJ1930Page — Endeudamiento con el exterior.
 *
 * Muestra los ForeignDebt confirmados que entran al CSV oficial, más una
 * sección de candidatos (proveedores internacionales con facturas que
 * todavía no se vincularon a un registro de deuda).
 */
import { useMemo, useState } from 'react'
import { Download, Eye, Info, AlertCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import apiClient from '@/lib/apiClient'
import DownloadButton from '@/components/DownloadButton'
import type { DJ1930Preview } from '../hooks/useForeignDebt'

function fmtMoney(value: string, currency = 'USD') {
  const n = parseFloat(value)
  if (isNaN(n)) return '—'
  return n.toLocaleString('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 })
}

export function DJ1930Page() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)
  const [preview, setPreview] = useState<DJ1930Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const years = useMemo(() => Array.from({ length: 8 }, (_, i) => currentYear - i), [currentYear])

  const onPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<DJ1930Preview>(`/treasury/dj-1930/${year}/preview/`)
      setPreview(data)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al cargar previsualización')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      {/* Título/subtítulo los provee TreasuryPage (PageHeader). */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-3">
          <label htmlFor="dj1930page-ano-comercial" className="text-sm text-content-muted">Año comercial</label>
          <select id="dj1930page-ano-comercial" value={year}
            onChange={(e) => { setYear(parseInt(e.target.value)); setPreview(null) }}
            className="px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content">
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={onPreview} disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-divider rounded-lg hover:bg-surface-hover disabled:opacity-50">
            <Eye size={14} /> {loading ? 'Cargando...' : 'Previsualizar'}
          </button>
          <DownloadButton path={`/treasury/dj-1930/${year}/export/`} filename={`DJ1930_${year}.csv`} />
        </div>
      </div>

      <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 text-sm text-content-subtle flex gap-3 shadow-card">
        <Info size={16} className="shrink-0 mt-0.5 text-primary" />
        <div className="space-y-1">
          <p>
            Las deudas se ingresan manualmente desde{' '}
            <a href="/treasury/foreign-debts" className="text-primary hover:underline">Deudas con el Exterior</a>.
            Acá se muestran los registros vigentes al 31/12 del año seleccionado.
          </p>
          <p className="text-xs">
            La sección "Candidatos" sugiere proveedores internacionales con facturas que aún no se
            vincularon a un registro de deuda. Son una pista para el contador, no entran al CSV.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger bg-danger/10 p-4 text-sm text-danger flex gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {preview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Deudas confirmadas" value={String(preview.totals.count)} />
            <StatCard label="Candidatos sugeridos" value={String(preview.totals.candidates_count)} />
            <StatCard label="Año" value={String(preview.year)} />
          </div>

          {preview.items.length === 0 ? (
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-8 text-center text-content-muted shadow-card">
              No hay deudas confirmadas vigentes al cierre del año {preview.year}.
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold text-content mb-2">Deudas confirmadas (entran al CSV)</h3>
              <DataTable
                columns={[
                  {
                    key: 'creditor_name', header: 'Acreedor',
                    render: it => (
                      <>
                        {it.creditor_name}
                        {it.is_related_party === 'S' &&
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-warning/20 text-warning">relac.</span>}
                      </>
                    ),
                  },
                  { key: 'creditor_country', header: 'País', render: it => <span className="font-mono text-content-subtle">{it.creditor_country}</span> },
                  { key: 'instrument_code', header: 'Instr.', render: it => <span className="text-content-subtle">{it.instrument_code}</span> },
                  { key: 'currency', header: 'Moneda', render: it => <span className="text-content-subtle">{it.currency}</span> },
                  { key: 'principal_amount', header: 'Capital', numeric: true, sortValue: it => Number(it.principal_amount), render: it => fmtMoney(it.principal_amount, it.currency) },
                  { key: 'outstanding_balance', header: 'Saldo', numeric: true, sortValue: it => Number(it.outstanding_balance), render: it => fmtMoney(it.outstanding_balance, it.currency) },
                  { key: 'interest_rate', header: 'Tasa', numeric: true, render: it => `${it.interest_rate}%` },
                  { key: 'disbursement_date', header: 'Desembolso', render: it => <span className="text-content-subtle">{it.disbursement_date || '—'}</span> },
                ]}
                rows={preview.items}
                rowKey={(_, i) => i}
                minWidth={900}
                caption="Deudas declaradas en la DJ 1930"
              />
            </div>
          )}

          {preview.candidates.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-content mb-2">Candidatos sugeridos</h3>
              <p className="text-xs text-content-muted mb-2">
                Proveedores marcados como internacionales con facturas del año {preview.year} sin vínculo a una deuda registrada.
              </p>
              <DataTable
                columns={[
                  { key: 'supplier_name', header: 'Proveedor' },
                  { key: 'supplier_tax_id', header: 'Tax ID', render: c => <span className="font-mono text-content-subtle">{c.supplier_tax_id || '—'}</span> },
                  { key: 'supplier_country', header: 'País', render: c => <span className="font-mono text-content-subtle">{c.supplier_country || '—'}</span> },
                  { key: 'invoice_count', header: 'Facturas', numeric: true },
                  { key: 'total_billed', header: 'Total facturado', numeric: true, sortValue: c => Number(c.total_billed), render: c => fmtMoney(c.total_billed, 'USD') },
                ]}
                rows={preview.candidates}
                rowKey={(_, i) => i}
                minWidth={760}
                caption="Proveedores internacionales sin deuda registrada"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 shadow-card">
      <div className="text-xs text-content-muted">{label}</div>
      <div className="text-lg font-bold text-content mt-1 tabular-nums">{value}</div>
    </div>
  )
}

export default DJ1930Page
